import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Form,
  Input,
  Select,
  Button,
  Space,
  Card,
  Divider,
  Row,
  Col,
  Switch,
  Radio,
} from "antd";
import {
  MinusCircleOutlined,
  PlusOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import {
  createMessageTemplate,
} from "@api/services/facebook";
import { TemplatePreview } from "../TemplatePreview";
import { ROUTES } from "@config/routes.config";
import { useNotificationContext } from "@hooks/useNotificationContext";
import styles from "./styles.module.less";
import type{ FormValues } from "@interfaces/setting.interface";

const { Option } = Select;
const { TextArea } = Input; 

export const TemplateCreate: React.FC = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { success, error } = useNotificationContext();
  const [loading, setLoading] = useState(false);
  const [templateType, setTemplateType] = useState<string>("GENERIC");

  const formValues = Form.useWatch([], form);

  useEffect(() => {
    form.setFieldsValue({
      type: "GENERIC",
      scope: "USER",
      status: true,
      media_type: "image",
      aspect_ratio: "horizontal",
    });
  }, [form]);

  const previewData = useMemo(() => {
    if (!formValues) return null;

    const values = formValues as FormValues;
    let payload: Record<string, unknown> = {};

    if (values.type === "GENERIC") {
      payload = {
        title: values.title,
        subtitle: values.subtitle,
        image_url: values.image_url,
        buttons: values.buttons,
      };
      if (values.default_action_url) {
        payload.default_action = {
          type: "web_url",
          url: values.default_action_url,
        };
      }
    } else if (values.type === "BUTTON") {
      payload = {
        text: values.text,
        buttons: values.buttons,
      };
    } else if (values.type === "MEDIA") {
      payload = {
        media_type: values.media_type || "image",
        media_url: values.media_url,
        attachment_id: values.attachment_id,
        aspect_ratio: values.aspect_ratio,
        buttons: values.buttons,
      };
    }

    return {
      type: values.type || "GENERIC",
      ...payload,
    };
  }, [formValues]);

  const handleTypeChange = (value: string) => {
    setTemplateType(value);
  };

  const handleSubmit = async (values: FormValues) => {
    try {
      setLoading(true);

      let payload: Record<string, unknown> = {};

      if (values.type === "GENERIC") {
        payload = {
          title: values.title,
          subtitle: values.subtitle,
          image_url: values.image_url,
          buttons: values.buttons,
        };
        if (values.default_action_url) {
          payload.default_action = {
            type: "web_url",
            url: values.default_action_url,
          };
        }
      } else if (values.type === "BUTTON") {
        payload = {
          text: values.text,
          buttons: values.buttons,
        };
      } else if (values.type === "MEDIA") {
        payload = {
          media_type: values.media_type || "image",
          media_url: values.media_url,
          attachment_id: values.attachment_id,
          aspect_ratio: values.aspect_ratio,
          buttons: values.buttons,
        };
      }

      const response = await createMessageTemplate({
        name: values.name || "",
        type: values.type || "GENERIC",
        payload,
        scope: values.scope || "USER",
        status: values.status ? "ACTIVE" : "INACTIVE",
      });

      if (response.code === 0) {
        success(
          "Tạo mẫu template thành công",
          "Mẫu template đã được tạo thành công.",
          3
        );
        navigate(ROUTES.SETTINGS.FACEBOOK_TEMPLATES);
      } else {
        error(
          "Tạo mẫu template thất bại",
          response.message || "Không thể tạo mẫu template",
          4
        );
      }
    } catch (err) {
      console.error("Error creating template:", err);
      error(
        "Lỗi hệ thống",
        "Đã xảy ra lỗi khi tạo mẫu template. Vui lòng thử lại.",
        4
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(ROUTES.SETTINGS.FACEBOOK_TEMPLATES);
  };

  const renderButtonsForm = () => (
    <Form.List name="buttons">
      {(fields, { add, remove }) => (
        <>
          {fields.map(({ key, name, ...restField }) => (
            <Card
              key={key}
              size="small"
              style={{ marginBottom: 8, background: "#fafafa" }}
            >
              <Row gutter={12}>
                <Col span={6}>
                  <Form.Item
                    {...restField}
                    name={[name, "type"]}
                    label="Loại"
                    rules={[{ required: true, message: "Chọn loại" }]}
                  >
                    <Select>
                      <Option value="web_url">URL</Option>
                      <Option value="postback">Postback</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    {...restField}
                    name={[name, "title"]}
                    label="Tiêu đề nút"
                    rules={[{ required: true, message: "Nhập tiêu đề" }]}
                  >
                    <Input placeholder="Xem thêm" maxLength={20} />
                  </Form.Item>
                </Col>
                <Col span={9}>
                  <Form.Item shouldUpdate>
                    {({ getFieldValue }) => {
                      const buttonType = getFieldValue(["buttons", name, "type"]);
                      return buttonType === "web_url" ? (
                        <Form.Item
                          {...restField}
                          name={[name, "url"]}
                          label="URL"
                          rules={[
                            { required: true, message: "Nhập URL" },
                            { type: "url", message: "URL không hợp lệ" },
                          ]}
                        >
                          <Input placeholder="https://example.com" />
                        </Form.Item>
                      ) : (
                        <Form.Item
                          {...restField}
                          name={[name, "payload"]}
                          label="Payload"
                          rules={[{ required: true, message: "Nhập payload" }]}
                        >
                          <Input placeholder="GET_STARTED" />
                        </Form.Item>
                      );
                    }}
                  </Form.Item>
                </Col>
                <Col
                  span={1}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    paddingTop: 30,
                  }}
                >
                  <MinusCircleOutlined
                    onClick={() => remove(name)}
                    style={{ color: "#ff4d4f", fontSize: 18, cursor: "pointer" }}
                  />
                </Col>
              </Row>
            </Card>
          ))}
          <Form.Item>
            <Button
              type="dashed"
              onClick={() => add({ type: "web_url" })}
              block
              icon={<PlusOutlined />}
              disabled={fields.length >= 3}
            >
              Thêm nút (tối đa 3)
            </Button>
          </Form.Item>
        </>
      )}
    </Form.List>
  );

  return (
    <div className={styles.templateFormPage}>
      <Row gutter={24} style={{ height: '100%' }}>
        <Col span={16} style={{ display: 'flex', flexDirection: 'column' }}>
          <Card
            title={
              <Space>
                Tạo Mẫu Tin nhắn Mới
              </Space>
            }
            className={styles.templateFormCard}
          >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            type: "GENERIC",
            scope: "USER",
            status: true,
            media_type: "image",
            aspect_ratio: "horizontal",
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Tên Mẫu Tin nhắn"
                rules={[{ required: true, message: "Vui lòng nhập tên mẫu!" }]}
              >
                <Input placeholder="VD: Chào mừng khách hàng mới" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="type"
                label="Loại Template"
                rules={[
                  { required: true, message: "Vui lòng chọn loại template!" },
                ]}
              >
                <Select onChange={handleTypeChange}>
                  <Option value="GENERIC">Generic (Card)</Option>
                  <Option value="BUTTON">Button (Nút bấm)</Option>
                  <Option value="MEDIA">Media (Hình ảnh/Video)</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="scope" label="Phạm vi">
                <Select disabled>
                  <Option value="USER">Cá nhân (USER)</Option>
                  <Option value="ADMIN">Hệ thống (ADMIN)</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="status"
                label="Trạng thái"
                valuePropName="checked"
              >
                <Switch checkedChildren="Hoạt động" unCheckedChildren="Tắt" />
              </Form.Item>
            </Col>
          </Row>

          <Divider>Nội dung Template</Divider>

          {templateType === "GENERIC" && (
            <>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="title"
                    label="Tiêu đề"
                    rules={[{ required: true, message: "Nhập tiêu đề" }]}
                  >
                    <Input
                      placeholder="Xin chào!"
                      maxLength={80}
                      showCount
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="subtitle" label="Phụ đề">
                    <Input
                      placeholder="Chào mừng bạn đến với dịch vụ"
                      maxLength={80}
                      showCount
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="image_url"
                    label="URL Hình ảnh"
                    rules={[{ type: "url", message: "URL không hợp lệ" }]}
                  >
                    <Input placeholder="https://example.com/image.jpg" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="default_action_url"
                    label="URL khi click vào card"
                    rules={[{ type: "url", message: "URL không hợp lệ" }]}
                  >
                    <Input placeholder="https://example.com" />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item label="Các nút hành động">
                {renderButtonsForm()}
              </Form.Item>
            </>
          )}

          {templateType === "BUTTON" && (
            <>
              <Form.Item
                name="text"
                label="Nội dung tin nhắn"
                rules={[{ required: true, message: "Vui lòng nhập nội dung!" }]}
              >
                <TextArea
                  rows={4}
                  placeholder="Bạn muốn làm gì tiếp theo?"
                  maxLength={640}
                  showCount
                />
              </Form.Item>
              <Form.Item label="Các nút hành động">
                {renderButtonsForm()}
              </Form.Item>
            </>
          )}

          {templateType === "MEDIA" && (
            <>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    name="media_type"
                    label="Loại Media"
                    initialValue="image"
                  >
                    <Radio.Group>
                      <Radio value="image">Hình ảnh</Radio>
                      <Radio value="video">Video</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="aspect_ratio"
                    label="Tỷ lệ khung hình"
                    initialValue="horizontal"
                  >
                    <Select>
                      <Option value="horizontal">Ngang (1.91:1)</Option>
                      <Option value="square">Vuông (1:1)</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="media_url"
                    label="URL Media"
                    rules={[{ type: "url", message: "URL không hợp lệ" }]}
                  >
                    <Input placeholder="https://example.com/image.jpg" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="attachment_id"
                    label="Attachment ID (tuỳ chọn)"
                  >
                    <Input placeholder="123456789..." />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item label="Các nút hành động">
                {renderButtonsForm()}
              </Form.Item>
            </>
          )}

        </Form>
      </Card>
        </Col>
        <Col span={8} style={{ display: 'flex', flexDirection: 'column' }}>
          <Card
            title="Xem trước trên Messenger"
            className={styles.templatePreviewCard}
          >
            <div className={styles.templatePreviewWrapper}>
              <TemplatePreview data={previewData || undefined} />
            </div>
          </Card>
        </Col>
      </Row>
      <div className={styles.templateFormFooter}>
        <Space>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={handleCancel}
            disabled={loading}
          >
            Quay lại
          </Button>
          <Button 
            type="primary" 
            onClick={() => form.submit()}
            loading={loading}
          >
            Tạo mới
          </Button>
        </Space>
      </div>
    </div>
  );
};

